// ============================================================================
// ui/share.js — the close: Save & share sheet + the promise-shaped quote modal
// ============================================================================
// Fable §5.4 (the quote-saved modal) + §5.7 (Save & share, unhidden). Built to
// the END-STATE shape now; wired only as far as the backend exists (§9.1 interim):
//   • Download image  — chrome-free front-view PNG (reveal.snapshotPNG), REAL today. Since
//                       2026-08-03 a BUYER's copy lands on a white sheet with the Blue Sky mark +
//                       domain under it (`ui/credit-image.js`) — the only exit from this tool that
//                       otherwise carried no way back to us. A REP still gets the bare transparent
//                       plate: their file goes INTO a proposal, and a white sheet can't composite.
//   • Share via…      — the OS share sheet (navigator.share), so a buyer can pick email, WhatsApp,
//                       Messages, AirDrop, whatever they actually use (Ryan 2026-07-30). Shown
//                       wherever the API EXISTS rather than on mobile only — it works on Chrome/Edge
//                       for Windows and Safari on macOS, and not in Firefox, so a viewport test would
//                       be wrong in both directions. ⚠ It needs transient activation: see
//                       `warmShareLink` for why the link is created when the card OPENS.
//   • Email my design — mailto: with a note. ⚠ NOT "email me": the mailto has no recipient, so it
//                       opens a blank compose window and nothing reaches anyone's inbox on its own.
//                       The "a copy in your inbox" promise belongs to the Resend-backed version below,
//                       which would also capture an address — the only row here with lead value.
//   • Copy link       — copies the designer link today; the view-only /share page
//                       (opens on the 3D reveal) is the backend follow-up.
// (A greyed "Download PDF — comes with your quote" row was listed here too; removed
// 2026-07-25 at Ryan's request, from the live canvas menu and from this sheet, so a
// revival of the sheet can't drag the placeholder back with it. /share/pdf earns a
// row when it exists.)
// The modal carries the trust headline ("nothing is made until you approve a
// production proof") so the copy survives the eventual wiring.
// ============================================================================

import { h } from './dom.js';
import { showWorking, isWorking, setWorkingProgress } from './working-scrim.js';
import { openModal } from './modal.js';
import { wdTrack, WD_EVENTS } from '../analytics.js';
import { isRepMode, getRep, PUBLIC_TOOL_PATH } from '../rep-mode.js';
import { isPlaceholderText } from '../layouts.js';
import { composeCreditImage } from './credit-image.js';
import { parseProductKey, categoryLabelOne, catalogueRank } from '../products.js';
import { designSpec } from '../design-spec.js';
import { renderHeadingPng } from './heading-image.js';
import { uploadDesignArtwork } from './artwork-upload.js';
import { portalToken, portalApi, isExternalPortalUser } from '../portal-api.js';


/* What the browser's save dialog offers (Ryan 2026-08-07). Singular vs PLURAL is the only difference
   and it is load-bearing: these two land in the same Downloads folder, and a buyer who saves both
   needs to tell them apart in a file list without opening either. Brand LAST so the distinguishing
   words come first — a truncated column still shows "Custom Weight Design…". */
const SINGLE_FILENAME = 'Custom Weight Design - Blue Sky Fitness Supply.png';
const SHEET_FILENAME = 'Custom Weight Designs - Blue Sky Fitness Supply.png';

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-10-30) — THE JPEG COPIES. *"I do want to give him the
   ability to render PNG's and JPEGs -- highish resolution images of any weights he generates"*, on
   plain white (his pick), for everyone in the portal. Same picture as the PNG, same size: a JPEG has
   no transparency, so the plate is laid on white and re-encoded. The name differs only in extension. */
const JPEG_QUALITY = 0.92;
const jpegName = (pngName) => pngName.replace(/\.png$/, '.jpg');

/** A PNG blob flattened onto white and re-encoded as JPEG, same pixel size. Null on any failure. */
export async function flattenToJpeg(blob) {
  try {
    const bmp = await createImageBitmap(blob);
    const cv = document.createElement('canvas');
    cv.width = bmp.width;
    cv.height = bmp.height;
    const g = cv.getContext('2d');
    g.fillStyle = '#ffffff';
    g.fillRect(0, 0, cv.width, cv.height);
    g.drawImage(bmp, 0, 0);
    bmp.close?.();
    return await new Promise((res) => cv.toBlob((b) => res(b || null), 'image/jpeg', JPEG_QUALITY));
  } catch {
    return null;
  }
}

/** Save `blob` as PNG, or as a white-backed JPEG when asked. A failed conversion still saves the PNG:
 *  the picture is the point, and the file's own extension says which one arrived. */
async function saveImage(blob, pngName, jpeg) {
  if (jpeg) {
    const jpg = await flattenToJpeg(blob);
    if (jpg) { downloadBlob(jpg, jpegName(pngName)); return; }
  }
  downloadBlob(blob, pngName);
}

/**
 * The two halves of a product's name — `{ kind, category }`, e.g. `{ kind: 'Competition Urethane',
 * category: 'Bumper Plate' }`.
 *
 * ⚠ RETURNED SPLIT, NOT JOINED (Ryan 2026-08-07). The contact sheet stacks them on two lines, which
 * is what let the type grow: one long joined string had to shrink to fit a cell, so the "·" was
 * costing readability on every tile. Callers that want one line join it themselves.
 */
function productNameParts(ctx, key) {
  if (!key) return { kind: '', category: '' };
  const pick = (ctx.pickedProducts?.() || []).find((p) => p.key === key);
  if (pick) return { kind: pick.kind || '', category: pick.category || '' };
  try {
    const { kind, category } = parseProductKey(key);
    return { kind: kind || '', category: categoryLabelOne(category) || '' };
  } catch { return { kind: '', category: '' }; }
}

/**
 * "Competition Urethane · Bumper Plate" for a product key.
 *
 * ⚠ READ OFF THE PICKED SET FIRST so the single image and the contact-sheet tile are the IDENTICAL
 * string — two formatters would drift the day either changed, and these two files are meant to be
 * read side by side. The derivation is only the fallback for a design with no picks behind it (a
 * resumed session, a shared `?d=` link), where the set is empty but the product is still known.
 */
function productLabelFor(ctx, key) {
  const { kind, category } = productNameParts(ctx, key);
  return [kind, category].filter(Boolean).join(' · ');
}

/** Grab a chrome-free front-view PNG blob (or null in the WebGL-fallback). */
async function snapshotBlob(ctx, opts) {
  try { return (await ctx.snapshotPNG?.(opts)) || null; } catch { return null; }
}

/**
 * How big a plate image the DESIGN SHEET asks for — much smaller than the downloadable one.
 *
 * Not a guess and not a tuning knob: the sheet draws the plate into a 300pt-tall box, so ~1250px
 * is already 300dpi at print size and every pixel past that is weight nobody sees. The full-res
 * portrait (3072² before cropping, ~2.1MB) base64s to ~2.8MB and rfq-worker's GLOBAL body cap is
 * 2MB — it came back `413 Request body too large`, which is the right answer from the worker and
 * the wrong question from us. The cap is deliberately not raised (memory
 * `outlook-composer-inline-image-hoist`: "never raise MAX_BODY_LENGTH").
 */
const PDF_SNAPSHOT_PX = 1400;
/** Base64 budget for the sheet's image, comfortably inside the worker's 2MB body cap. */
const PDF_SNAPSHOT_B64_MAX = 1_300_000;

/** Trigger a browser download of a blob. */
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = h('a', { href: url, download: filename, style: { display: 'none' } });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** The bare designer URL — where a share link is built from, and the fallback if one can't be.
 *
 *  In REP MODE this must not be the page we are standing on. The portal edition lives at
 *  /pages/rep-weight-designer behind the portal's auth gate, so a link built from
 *  `location.pathname` would send a committee to a sign-in screen — the exact failure the
 *  share feature was built to end, wearing a different hat. */
function designerLink() {
  try {
    if (isRepMode()) return location.origin + PUBLIC_TOOL_PATH;
    return location.origin + location.pathname;
  } catch { return 'https://weight-designer.pages.dev/'; }
}

// ── real share links (Phase D2, 2026-07-26) ──────────────────────────────────
// The worker stores the design and names it; the link is this page plus that name.
//
// NO NEW PAGE, and that is the main design decision here. §8's D2 line says the view-only
// surface "opens on the 3D reveal" — which is this app, so a share link is
// `/pages/weight-designer?d=<id>` and the app hydrates from the id. A separate /share route
// would mean a second Shopify page, a second template pin, a second deploy target and a
// second renderer to keep in step with every future change to the plate.
//
// What travels is the app's OWN AUTOSAVE ENVELOPE — the exact string localStorage holds for
// Resume. So the recipient's app restores a shared design through `fromAutosave`, the same
// path a returning buyer's own design takes. One restore path, no share-specific schema, and
// nothing new to update when the profile grows a field.
const SHARE_ENDPOINT = 'https://rfq-worker.ryan-29b.workers.dev/wd-share';
export const SHARE_PARAM = 'd';

/**
 * Persist the current design and return its share URL, or null if it couldn't be stored.
 * `ctx.shareEnvelope()` hands over the autosave string (wired in main.js, which owns
 * `toAutosave`); the message on a refusal is the worker's own, because it names the way out
 * ("use Download image") and only the worker knows which limit was hit.
 */
/**
 * Every model this buyer has actually DESIGNED, in the order the switcher shows them — the live one
 * plus each product they visited and left. `[]` when there is only one, so a single-product share
 * writes exactly the record it always did.
 *
 * ⛔ GATED ON ONE IDENTITY, which is the whole reason `captureOf` stamps `idKey`. Swap the logo
 * after leaving the bumper and the stored bumper still wears the old mark — shared unchecked, that
 * is a "set" showing a committee two different schools. The live design is the identity that counts,
 * because it is the one on screen when they press Share.
 */
/* ⛔ EXPORTED so the QUOTE handoff writes the same set (2026-08-24). `/wd-quote` was storing the
   pre-Phase-1b single-model record, so a buyer who designed dumbbells AND grip plates reached the
   rep — and through them the customer — as one plate with no arrows. Sharing the builder rather
   than repeating it: "what the set IS" has already been got wrong once (see the note above) and
   two copies of that rule would drift the next time it is refined. */
export function designSet(ctx) {
  let live = null;
  let caps = [];
  try { live = ctx.liveDesign?.() || null; } catch { live = null; }
  try { caps = ctx.capturedDesigns?.() || []; } catch { caps = []; }

  const byProduct = new Map();
  for (const c of caps) if (c && c.env && c.product) byProduct.set(c.product, c);
  // The live one wins over its own older capture — it is the stage, not a snapshot of it.
  if (live && live.env && live.product) byProduct.set(live.product, live);

  const identity = live ? live.idKey : null;

  /* ⛔ THE SET IS WHAT THEY PICKED, NOT WHAT THEY HAPPENED TO VISIT (Ryan 2026-08-16, on a live share
     that showed no arrows).
       The first version of this built the set purely from per-product CAPTURES, which only exist for
     a model the buyer opened and then left. Pick four products, design two, share — and the link
     carried two, so a set that reads as four in the picker arrived as a pair. Worse, pick three and
     open only one and there is nothing to compare, so `members.length < 2` returned early and the
     recipient got no arrows at all: exactly the report.
       The picked list is the buyer's own statement of what the set IS, so it is the spine. A model
     with a capture carries its envelope; one without carries just its key, and the recipient's app
     puts it on the stage wearing the design that came with the link — which is what the tool does
     natively when you switch products, and is the whole "one identity across the family" premise. */
  const picks = (ctx.pickedProducts?.() || []).map((p) => p && p.key).filter(Boolean);
  const keys = picks.length ? picks : [...byProduct.keys()];
  // A set of one is just a design. Say nothing rather than write a one-item array.
  if (keys.length < 2) return [];

  return keys.map((key) => {
    const cap = byProduct.get(key);
    /* ⚠ An envelope only rides along if it belongs to the identity on screen. `captureOf` stamps
       `idKey` precisely so a logo swapped after leaving the bumper cannot ship a committee two
       different schools — a stale capture is dropped to a bare key, and the model still appears,
       wearing the current design rather than the old one. */
    return (cap && cap.env && (!identity || cap.idKey === identity))
      ? { product: key, design: cap.env }
      : { product: key };
  });
}

async function storeDesign(ctx) {
  const envelope = ctx.shareEnvelope?.();
  if (!envelope) return null;
  const p = ctx.state?.designProfile || {};
  const set = designSet(ctx);
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-10-28) — a rep's link carries the rep's sign-in, so the
     worker can put their name, phone and email under the presented plate. Never sent by a buyer, and
     a missing token just means an unstamped link — this route stays public. */
  let auth = '';
  /* CUSTOM_PORTALACCESS_BY_CLAUDE — an external user's link is never stamped (no outsider's contact
     under our logo), and his token on this public route would only be refused and audited. */
  if (isRepMode() && !isExternalPortalUser()) {
    try { const t = await portalToken(); if (t) auth = `Bearer ${t}`; } catch { /* unstamped, still shared */ }
  }

  const post = (withSet) => fetch(SHARE_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(auth ? { Authorization: auth } : {}) },
    body: JSON.stringify({
      design: envelope,
      ...(withSet && set.length ? { set } : {}),
      meta: { orgName: p.orgName || '', product: ctx.state?.ui?.product || '', source: 'weight-designer' },
    }),
  });

  let res;
  try {
    res = await post(true);
    /* ⛔ A SET TOO BIG TO STORE MUST NOT COST THE SHARE. Every envelope carries the logo as base64,
       so a four-product set is four copies of it and the worker's 6MB total cap is reachable. The
       retry drops the extra models and keeps the plate they were looking at — the same trade
       `/wd-quote` already makes when it re-POSTs with `design: null` rather than lose a priced-out
       lead. Silence would be the wrong half to keep: better one model than a dead link. */
    if (res.status === 413 && set.length) res = await post(false);
  } catch {
    return null; // offline / blocked — the caller falls back to the plain designer link
  }
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON body */ }
  if (!res.ok || !data || !data.ok || !data.id) {
    if (data && data.message) ctx.status?.info?.(data.message);
    return null;
  }
  // ⚠ `design_shared` is fired by the CALLERS, not here (Ryan 2026-07-30, "Share via").
  // This is a storage primitive and the event is an intent, and the two came apart the moment the
  // share link started being WARMED ahead of the tap: warming runs when the Share card opens — which
  // a mouse does on a hover-peek — so tracking here would have counted every hover of the Share
  // button as a share and quietly inflated the metric. The three surfaces that genuinely share
  // (Copy link, Email, Share via) each fire it on success instead, which is also strictly better
  // attribution than one event for all of them.
  return data.id;
}

/**
 * Persist the design and return its share URL, or null.
 *
 * The id and the URL were one function until the design sheet needed the id on its own
 * (the PDF endpoint reads the design from storage rather than from the request body, so
 * the sheet and the link can never disagree about what was designed). Kept as one storage
 * path with two readers rather than a second POST: two would mean two KV records for one
 * design, and the sheet could outlive or predate the link it prints.
 */
async function createShareLink(ctx) {
  const id = await storeDesign(ctx);
  if (!id) return null;
  const base = designerLink();
  return `${base}${base.includes('?') ? '&' : '?'}${SHARE_PARAM}=${encodeURIComponent(id)}`;
}

// ── WARMING THE SHARE LINK (Ryan 2026-07-30, for "Share via") ────────────────────────────────────
// `navigator.share()` requires TRANSIENT USER ACTIVATION, and `createShareLink` is a worker round
// trip that takes a visible moment on a multi-MB logo. Await the link and then call share() and iOS
// Safari rejects it with `NotAllowedError` — the activation is spent. The API accepts no promise, so
// there is no way to hand it a link that is still arriving; the tap itself has to be synchronous.
//
// So the link is created when the Share card OPENS and cached here, and the tap reads the cache.
//
// ⚠ KEYED ON THE DESIGN'S CONTENT, which is what makes the cache safe rather than a staleness bug in
// waiting. Warm the link, edit the plate, tap Share, and a naive cache would hand out a URL pointing at
// the design from before the edit — silently, and only the recipient would ever find out. Deriving the
// key from the envelope answers "is this still the same design?" exactly, with no invalidation hook for
// a future edit path to forget to call.
//
// ⚠ …BUT NOT ON THE ENVELOPE STRING ITSELF, which was the first version and never hit the cache once:
// `toAutosave` stamps `ts: Date.now()`, so the string differs on every call. Every open re-POSTed, and
// — the part that actually mattered — every tap fell through to the cold `await` path this whole
// mechanism exists to avoid, which on iOS is the NotAllowedError. It measured as 3 POSTs for one share
// (`tools/verify-share-via.mjs`) and would never have been visible in the UI.
//   So `ts` is neutralised before fingerprinting. If that field is ever renamed the key simply stops
// matching and we do an extra POST — the failure direction is a wasted request, never a stale link.
function designFingerprint(envelope) {
  // FNV-1a over the timestamp-neutralised envelope. Hashed rather than kept, because the envelope is
  // multi-MB with a base64 logo in it and this is called on every hover-peek of the Share button.
  const s = String(envelope).replace(/"ts":\s*\d+/, '"ts":0');
  let hash = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    hash ^= s.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${(hash >>> 0).toString(36)}:${s.length}`; // length too — a cheap second dimension
}

/**
 * The cache key for a warmed link: the live design AND the rest of the set, because both are in the
 * link now. Design one model, hover Share, design a second, tap Share — keyed on the live envelope
 * alone the buyer would send a link missing the model they had just finished.
 *
 * ⚠ THE SET HALF IS DELIBERATELY NOT `designSet()`. That reads `liveDesign()`, which calls
 * `toAutosave` — a second multi-MB serialize, on a function that runs on every hover-peek of the
 * Share button. The captures are already-serialized strings sitting in memory, so their products
 * and lengths cost nothing, and the LIVE design's content is already covered by the envelope hash
 * this is appended to.
 */
function shareFingerprint(ctx, envelope) {
  let caps = [];
  try { caps = ctx.capturedDesigns?.() || []; } catch { caps = []; }
  const sig = caps.map((c) => `${c.product}:${c.env.length}:${c.idKey}`).join('|');
  return `${designFingerprint(envelope)}#${ctx.state?.ui?.product || ''}#${sig}`;
}

let warm = { key: null, url: null, promise: null };

/** The warmed link IF it still describes the current design — else null. Synchronous, for the tap. */
function warmLinkNow(ctx) {
  const envelope = ctx.shareEnvelope?.();
  if (!envelope) return null;
  return warm.key === shareFingerprint(ctx, envelope) ? warm.url : null;
}

/**
 * Store the design and cache its link, ready for a synchronous share. Idempotent and cheap to call
 * repeatedly: a matching envelope returns the cached URL (or the in-flight promise) without a second
 * POST, which matters because a hover-peek can open the Share card many times in a few seconds.
 */
export function warmShareLink(ctx) {
  const envelope = ctx.shareEnvelope?.();
  if (!envelope) return Promise.resolve(null);
  const key = shareFingerprint(ctx, envelope);
  if (warm.key === key) {
    if (warm.url) return Promise.resolve(warm.url);
    if (warm.promise) return warm.promise; // already in flight — join it rather than POST twice
  }
  const promise = createShareLink(ctx).then((url) => {
    // Only publish into the cache if the design hasn't moved on under us while the POST was in
    // flight — otherwise a slow request for an old design could overwrite a newer link.
    if (warm.key === key) warm.url = url;
    return url;
  }).catch(() => null);
  warm = { key, url: null, promise };
  return promise;
}

/**
 * Is the OS share sheet available at all?
 *
 * FEATURE-DETECTED, NEVER PLATFORM-SNIFFED (Ryan asked whether this is mobile-only). It is not: this
 * is true on Chrome/Edge on Windows and Safari on macOS, and false on Firefox desktop — so a
 * `matchMedia` mobile check would both hide a working button and show a throwing one. One rule covers
 * essentially all mobile plus the desktops that can actually do it, and "Copy link" stays present
 * everywhere as the primitive that always works (and is the better tool on a desktop anyway).
 *   Also requires a secure context, which `navigator.share` being undefined on http already implies.
 */
export function canWebShare() {
  try { return typeof navigator !== 'undefined' && typeof navigator.share === 'function'; } catch { return false; }
}

/* The share BODY, hoisted out of `shareViaSystem` so the sent card's share button and the canvas
   cluster's cannot drift into two descriptions of the same act. */
const SHARE_TEXT = 'Check out my custom 3D weight design from Blue Sky Fitness Supply — open the link to edit it yourself';

/** The share TITLE — Ryan's email-subject format verbatim, so the two surfaces cannot drift. */
function designShareTitle(ctx) {
  return ['3D Weight Design', designIdentityName(ctx), 'Blue Sky Fitness Supply'].filter(Boolean).join(' - ');
}

/**
 * Hand the design to the OS share sheet — email, WhatsApp, Messages, AirDrop, whatever they have.
 *
 * `text` deliberately does NOT contain the URL: every target gets `url` as its own field and most
 * append it themselves, so embedding it too reads as the link twice. It also carries no trailing
 * punctuation, because the handful of targets that drop the url field would otherwise show a sentence
 * ending in a colon and nothing after it.
 */
export async function shareViaSystem(ctx) {
  if (!canWebShare()) return false;
  /* ⚠️ THE DIALOG SPENDS THE TAP'S ACTIVATION, and its own confirm click replaces it. That is why
     `pickShareUrl` mints while the dialog is open: the click that closes it is a fresh activation
     and there is no fetch left to await. Where the mint is still in flight we fall through to the
     `NotAllowedError` recovery below, which copies the link and says so. */
  const picked = await pickShareUrl(ctx, 'Share');
  if (picked && picked.cancelled) return false;
  if (picked && picked.failed) { ctx.status?.info?.(picked.message); return false; }
  // The fast path, and the only one that is guaranteed to satisfy iOS: the card's open already warmed
  // this, so there is no await between the buyer's tap and `navigator.share`.
  let url = (picked && picked.url) || warmLinkNow(ctx);
  if (!url) url = await warmShareLink(ctx); // cold (a fast tap, or a failed warm) — try anyway
  if (!url) {
    ctx.status?.info?.('Couldn’t create a share link. Try “Download image” or “Copy link”.');
    return false;
  }
  try {
    await navigator.share({ title: designShareTitle(ctx), text: SHARE_TEXT, url });
    wdTrack(WD_EVENTS.SHARED);
    return true;
  } catch (err) {
    // Dismissing the OS sheet is an AbortError and is NOT a failure — say nothing at all. A toast
    // here would scold every buyer who opened the sheet and changed their mind.
    if (err && err.name === 'AbortError') return false;
    // Anything else is almost always `NotAllowedError`: the await above spent the activation. Don't
    // dead-end on it — the link exists and is good, so put it on the clipboard and say so. The buyer
    // gets what they wanted by one extra paste instead of a button that did nothing.
    const ok = await copyText(url);
    ctx.status?.info?.(ok ? 'Link copied — paste it wherever you like.' : url);
    return false;
  }
}

async function copyText(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* fall through */ }
  return false;
}

/**
 * Download the current design as a PNG. Returns true on success.
 *
 * ⚠ THE ONLY SNAPSHOT CONSUMER THAT GETS THE BLUE SKY FOOTER, and deliberately so:
 * this is the one artifact that leaves with no link back on it (Copy link, Share
 * via… and Email all carry a URL; a PNG in a booster-club email carries nothing).
 * `composeCreditImage` is called HERE rather than inside `snapshotPNG` because the
 * design sheet, the quote-saved thumbnail, the R2 showcase batch and the mobile
 * card picker all pull the same snapshot and every one of them must stay clean.
 * It never throws — a failure hands back the bare plate, so the download stands.
 *
 * ⛔ BUYERS ONLY (Ryan 2026-08-03). A REP gets the bare transparent, alpha-cropped
 * plate, and the reason is what the two people do with the file. The footer exists
 * to give an anonymous image a route back to us — which is the whole point when a
 * buyer forwards it to a committee, and in the way when a rep is placing the plate
 * into a proposal, a deck or a co-branded sheet and doing the branding themselves.
 * A white sheet cannot be composited over anything; transparency is the rep's
 * working asset. Same shape as the other three things rep mode changes (the PDF
 * button, public share links, `wdTrack` counting nothing) — see `rep-mode.js`.
 */
export async function downloadDesignImage(ctx, { jpeg = false } = {}) {
  /* ⛔ REFUSE A SECOND CLICK OUTRIGHT (Ryan 2026-08-07). The render poses a 3D stage and restores it;
     two overlapping runs photograph each other's setup. The 4-5 second wait is exactly long enough
     for an impatient buyer to click twice. */
  if (isWorking()) return false;
  const done = showWorking(ctx.el, 'Preparing your image', {
    onTimeout: () => ctx.status?.error?.("That took longer than expected — try Download image again."),
  });
  /* ⛔ NO STATUS PILL ON THIS PATH (Ryan 2026-08-07). The scrim says the same thing with more room,
     and the browser's own save dialog is the success confirmation. `info` below stays: it is the only
     way a buyer on a device that cannot render learns why nothing happened. */
  try {
    const key = ctx.state?.ui?.product;
    const envelope = ctx.shareEnvelope?.();
    let blob = null;

    /* ⚠ THE POSED RENDER, NOT THE LIVE STAGE (Ryan 2026-08-07: *"make sure the single image render
       uses the angled dumbbells/fixed barbells"*). `snapshotPNG` photographs the stage as it stands,
       which is face-on — right for a disc, and for a dumbbell or a fixed barbell it is a black disc
       with a stub behind it. `packet-poses.js` already answers this: a dumbbell gets a PAIR and a bar
       a diagonal hero, "so you can appreciate how cool looking the entire model is".
         Routing the single download through the SAME oven the contact sheet uses means the two are
       now literally the same picture — a buyer who downloads one plate and then the sheet cannot get
       two different photographs of one design, which they could until today. */
    if (key && envelope) {
      try {
        const { renderProductStills } = await import('../plate-batch.js');
        const { stills } = await renderProductStills(ctx, {
          envelope,
          products: [{
            key,
            weight: ctx.state?.designProfile?.weightValue,
            unit: ctx.state?.ui?.unit || 'lb',
            bodyColor: ctx.state?.designProfile?.bodyColor || null,
            label: productLabelFor(ctx, key),
          }],
        });
        if (stills.length) blob = stills[0].blob;
      } catch { /* fall through to the live stage below */ }
    }

    /* The live stage is the FALLBACK, not the path. It still covers a design with no envelope yet and
       any browser where building a second WebGL context fails — in both cases a face-on picture is
       enormously better than no download. */
    if (!blob) blob = await snapshotBlob(ctx);
    if (!blob) { ctx.status?.info?.('Open it on a computer to download the image — or take a screenshot.'); return false; }

    await saveImage(isRepMode() ? blob : await composeCreditImage(blob), SINGLE_FILENAME, jpeg);
    return true;
  } finally {
    done();
  }
}


/** A grid icon, for the all-products sheet — distinct from the single-plate arrow. */
export const ICON_GRID = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>';

/**
 * EVERY PICKED PRODUCT ON ONE PNG (Ryan 2026-08-07).
 *
 * One tile per product — his call, asked before building: *"1 tile per product please."* Not per
 * denomination: in his vocabulary "weights" means the MODELS, and a sheet that expanded three picks
 * into thirty tiles would answer a question nobody asked.
 *
 * ⚠ THIS IS THE SLOW ONE — seconds PER PRODUCT, because each needs its own offscreen 3D oven built
 * around its geometry. So unlike the single download it reports PROGRESS ("Rendering 3 of 8") and
 * offers CANCEL; a spinner alone cannot carry 30 seconds. Both come free: `renderProductStills`
 * already takes an `onProgress` and an `AbortSignal`.
 *
 * ⚠ AND IT RAISES THE SCRIM'S WATCHDOG. The default ceiling is tuned for a single ~5s render; ten
 * products can legitimately outrun it, and a watchdog that fires mid-render would tear the scrim off
 * a job that is working — the buyer would then be clicking into a live app while the oven is still
 * shooting, which is the exact corruption the scrim exists to prevent.
 *
 * Never throws.
 */
export async function downloadAllDesignImages(ctx, { jpeg = false } = {}) {
  if (isWorking()) return false;
  const picks = (ctx.pickedProducts?.() || []).filter((p) => p && p.key);
  if (picks.length < 2) return downloadDesignImage(ctx, { jpeg }); // nothing to tile — fall back to the one
  const envelope = ctx.shareEnvelope?.();
  if (!envelope) { ctx.status?.info?.('Add a logo or some text first — there is nothing to render yet.'); return false; }

  const controller = new AbortController();
  const total = picks.length;
  const done = showWorking(ctx.el, `Rendering your ${total} weights`, {
    // Generous, and derived rather than guessed: a slow machine can take several seconds per
    // product, so the ceiling has to scale with the set or a big order trips it every time.
    maxMs: 20000 + total * 20000,
    onCancel: () => controller.abort(),
    cancelLabel: 'Cancel',
    onTimeout: () => ctx.status?.error?.('That took longer than expected — try again, or download them one at a time.'),
  });

  try {
    const { renderProductStills } = await import('../plate-batch.js');
    const { composeContactSheet } = await import('./contact-sheet.js');
    const weight = ctx.state?.designProfile?.weightValue;
    const unit = ctx.state?.ui?.unit || 'lb';
    const bodyColor = ctx.state?.designProfile?.bodyColor || null;

    const byKey = new Map(picks.map((p) => [p.key, p]));
    const { stills, failures, aborted } = await renderProductStills(ctx, {
      envelope,
      signal: controller.signal,
      products: picks.map((p) => ({
        key: p.key,
        weight,
        unit,
        bodyColor,
        // Two lines on the tile: the KIND leads (it is what differs between tiles), the CATEGORY
        // sits under it. See `productNameParts` for why they travel split.
        label: p.kind || '',
        sub: p.category || '',
      })),
      onProgress: (i, n, label) => setWorkingProgress(`Rendering ${Math.min(i + 1, n)} of ${n}`, label),
    });

    if (aborted) return false;
    if (!stills.length) {
      ctx.status?.info?.('Couldn’t render your weights here — try downloading them one at a time.');
      return false;
    }

    const sheet = await composeContactSheet(stills.map((s) => {
      const p = byKey.get(s.productKey);
      return { blob: s.blob, label: p ? p.kind : s.label, sub: p ? p.category : '' };
    }));
    if (!sheet) { ctx.status?.info?.('Couldn’t build the image sheet — try downloading them one at a time.'); return false; }
    /* ⚠ BRANDED FOR EVERYONE, INCLUDING REPS — deliberately unlike the single download. A rep gets
       the bare transparent plate there because their file goes INTO a proposal and a white sheet
       cannot composite over anything (2026-08-03). This artefact is already an opaque white sheet
       with labels: it is a finished handout, not a compositable asset, so the rationale for
       stripping the brand does not reach it. Flag if that call is wrong. */
    const branded = await composeCreditImage(sheet);
    await saveImage(branded || sheet, SHEET_FILENAME, jpeg);
    /* ⚠ A PARTIAL SHEET SAYS SO. Silently shipping 6 of 8 tiles is the worst outcome here: the buyer
       forwards it to a committee believing it is their whole set, and the two missing products read
       as products we do not make. */
    if (failures.length) {
      ctx.status?.info?.(`${failures.length} of ${total} couldn’t be rendered — download those individually.`);
    }
    return true;
  } catch {
    ctx.status?.info?.('Couldn’t build the image sheet — try downloading them one at a time.');
    return false;
  } finally {
    done();
  }
}

// ── the design sheet (rep/portal only) ───────────────────────────────────────
const PDF_ENDPOINT = 'https://rfq-worker.ryan-29b.workers.dev/wd-pdf';

/** Which documents lead with the shared brand cover. ⚠ The worker's `PDF_VARIANTS[…].cover` is the
 *  authority; this is the one bit of it the client needs and cannot import across the repo line. */
const doc_cover = (variant) => variant !== 'vendor';

/** Blob → bare base64 (no data: prefix), or null. */
function blobToBase64(blob) {
  return new Promise((resolve) => {
    try {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result || '').replace(/^data:[^,]*,/, ''));
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    } catch { resolve(null); }
  });
}

/**
 * The VENDOR sheet's pictures: one render per product the design covers, ONE PAGE EACH.
 *
 * Ryan 2026-09-02: *"I want all the details from each weight on the PDF to fit on one page. so if
 * we're ordering dumbbells and grip plates, 2 pages.. if we're ordering bumpers and dumbbells and
 * grip plates, 3 pages."*
 *
 * ⛔ THIS USED TO COMPOSE ONE CONTACT SHEET, and the reason it did was the body cap, not the
 * layout: `/wd-pdf` is held at 2MB deliberately and the house rule is to shrink what we send
 * rather than raise a cap (memory `outlook-composer-inline-image-hoist`). That constraint has not
 * gone away — it is now met by SIZING each still to the number of them rather than by merging them
 * into one picture. A tiled sheet of three plates above one shared spec table made the factory work
 * out which line belonged to which item, which is the confusion the per-page shape removes.
 *
 * ⚠ THE BUDGET IS THE WHOLE DESIGN HERE. Each still is drawn into a 240pt-tall box, where ~1000px
 * is already past 300dpi, so the ladder below costs nothing visible and keeps a four-product order
 * inside the cap with room to spare.
 *
 * Falls back to the single plate on ANY failure, exactly as before: a vendor sheet picturing one
 * product is worth having, one that fails to generate is not.
 */
const VENDOR_STILL_PX = { 1: 1400, 2: 1200, 3: 1000 };
const VENDOR_STILL_PX_MANY = 900;

async function vendorSheetPages(ctx) {
  const picks = (ctx.pickedProducts?.() || []).filter((p) => p && p.key);
  const envelope = ctx.shareEnvelope?.();
  if (!picks.length || !envelope) return null;

  /* ⛔ ONE PRODUCT IS PHOTOGRAPHED BY THE LIVE STAGE, NOT THE OVEN — and this is a REGRESSION FIX,
     not an optimisation (Ryan 2026-09-02: *"the weight rendering appears to be much duller than
     normal. Like the logos/colors/text/weight number"*).

     Before the per-page rewrite this function read
       `if (picks.length < 2 || !envelope) return snapshotBlob(ctx, { size: PDF_SNAPSHOT_PX });`
     so a single-product sheet had ALWAYS used the live canvas. Routing every count through
     `renderProductStills` sent that case off-screen for the first time, and the off-screen render
     does not reproduce the live stage's look — the divergence the packet path has hit before.

     ⚠ ONLY WHEN IT IS THE PRODUCT ON SCREEN. `snapshotPNG` photographs the LIVE stage, so asking it
     for a page about some other product would return the wrong plate — confidently, with no error.
     ⚠ AND ONLY FOR A SINGLE PAGE. Mixing one live-rendered page with oven-rendered siblings would
     put a bright plate next to duller ones in the same document, which reads worse than either look
     applied consistently. Two or more products keep the oven, exactly as they did before today. */
  const livePick = picks.length === 1 && picks[0].key === ctx.state?.ui?.product ? picks[0] : null;
  if (livePick) {
    const blob = await snapshotBlob(ctx, { size: PDF_SNAPSHOT_PX });
    const png = blob ? await blobToBase64(blob) : null;
    if (png) {
      return [{ png, product: [livePick.kind, livePick.category].filter(Boolean).join(' · ') }];
    }
    // No live snapshot (a WebGL fallback) — fall through and let the oven try.
  }

  try {
    const { renderProductStills } = await import('../plate-batch.js');
    const weight = ctx.state?.designProfile?.weightValue;
    const unit = ctx.state?.ui?.unit || 'lb';
    const bodyColor = ctx.state?.designProfile?.bodyColor || null;
    const byKey = new Map(picks.map((p) => [p.key, p]));

    const { stills } = await renderProductStills(ctx, {
      envelope,
      products: picks.map((p) => ({ key: p.key, weight, unit, bodyColor, label: p.kind || '', sub: p.category || '' })),
      onProgress: (i, n) => setWorkingProgress(`Rendering ${Math.min(i + 1, n)} of ${n}`, ''),
    });
    if (!stills.length) return null;

    const edge = VENDOR_STILL_PX[stills.length] || VENDOR_STILL_PX_MANY;
    const out = [];
    for (const st of stills) {
      const p = byKey.get(st.productKey);
      /* The same string the contact sheet used to letter under each tile, so the sheet and the
         spec row cannot disagree about what a product is called. */
      const product = [p ? p.kind : st.label, p ? p.category : ''].filter(Boolean).join(' · ');
      const shrunk = (await downscaleBlob(st.blob, edge)) || st.blob;
      const png = await blobToBase64(shrunk);
      /* ⛔ THE PAGE SURVIVES A FAILED IMAGE. A product that cannot be encoded still gets its page,
         with its spec and colours and no picture — dropping the entry would delete the product
         from a build sheet, which is a far worse failure than a missing thumbnail. */
      out.push({ png: png || '', product });
    }
    return out.length ? out : null;
  } catch {
    return null;
  }
}

/** Re-encode a PNG blob so its longest edge is at most `maxEdge`. Null on any failure. */
async function downscaleBlob(blob, maxEdge) {
  try {
    const bmp = await createImageBitmap(blob);
    const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
    if (scale >= 1) { bmp.close?.(); return blob; }
    const cv = document.createElement('canvas');
    cv.width = Math.round(bmp.width * scale);
    cv.height = Math.round(bmp.height * scale);
    const g = cv.getContext('2d');
    g.imageSmoothingQuality = 'high';
    g.drawImage(bmp, 0, 0, cv.width, cv.height);
    bmp.close?.();
    return await new Promise((res) => cv.toBlob(res, 'image/png'));
  } catch {
    return null;
  }
}

/** A safe, recognisable filename stem from whatever the rep typed. */
function slug(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
}

/** Today, as `YYYY-MM-DD` — the date half of Ryan's filename convention. */
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * What a document is CALLED, for the fallback filename only.
 *
 * ⚠ Deliberately duplicated from the worker's `PDF_VARIANTS[…].docName`, and worth the duplication:
 * the normal path takes the name from the server (Content-Disposition), so these two strings only
 * ever meet on the storage-failed branch. Importing across the app/worker boundary is not possible,
 * and a shared constant shipped in the bundle would go stale just as easily. Add a case here when a
 * new document type lands — the fallback then reads right instead of saying "Weight Approval Sheet"
 * on something else.
 */
function pdfDocName(variant) {
  if (variant === 'approval') return 'Weight Approval Sheet';
  if (variant === 'vendor') return 'Vendor Ordering Sheet';
  return 'Weight Design Sheet';
}

/**
 * Generate the design sheet PDF and download it. Rep/portal only.
 *
 * The generator has existed and been deployed since 2026-07-26 with no button, because
 * Ryan's call was that the button belongs on the portal edition (changelog 99). This is
 * that button's other half.
 *
 * Order matters: the design is STORED first and the sheet is built from the stored copy
 * — `POST /wd-pdf` takes a share id, not a design — so the PDF, the view-only link
 * printed inside it, and anything the rep pastes later are all the same record.
 *
 * The snapshot has to come from here: a Worker has no WebGL and cannot render the plate.
 * Absent one the worker still returns a sheet that leads with the spec, which is far
 * better than failing the document because a rep is on a machine where 3D didn't come up.
 */
/* CUSTOM_BALLAST_BY_CLAUDE (Ryan 2026-09-01) — `po` and `openResult`, for the Deal Dashboard's
   generate-at-PO-time path.
     `po` is `{quoteRef, designId, poVendor}` and is the sheet's own REGISTRATION: the worker writes
   its R2 key back against that design so the purchase order can find it. Without it a sheet is a
   download and nothing more — which is what every sheet was until now, and why a PO could never
   attach one (the R2 key is built from a freshly-minted share id that no deal record has ever
   held). Omitted entirely by the menu path, which behaves exactly as it always has.
     `openResult: false` suppresses the new tab. The DD drives this in a popup that closes itself,
   and a PDF tab left behind from a window the rep did not open reads as a bug, not a courtesy. */
export async function downloadDesignSheet(ctx, { customerName = '', contactName = '', variant = 'approval', allProducts = false, admin = false, po = null, openResult = true } = {}) {
  const say = (m) => ctx.status?.info?.(m);
  const id = await storeDesign(ctx);
  if (!id) { say('Couldn’t save the design, so there’s nothing to build a sheet from.'); return false; }

  /* THE VENDOR SHEET'S ARTWORK LINKS (Ryan 2026-08-27) — best-effort, and fired here rather than
     at submit BECAUSE this is a different moment with a different answer. `LOGO-ARTWORK-RETENTION-
     SPEC.md` keeps the buyer's original out of a browsing session on purpose ("the original is
     retained at SUBMIT, so a session that never asks for a price leaves nothing behind"); an admin
     pressing "build the sheet I send the factory" is the opposite of a browsing session. What it
     CANNOT recover is an original this browser never held — a design opened from a share link has
     only the processed art, and the sheet says so rather than printing a dead link. The worker
     then falls back to the design record's own `meta.artwork` when the design came in by quote. */
  /* ⚠ SAY SOMETHING FIRST — this beat now includes an upscale round trip (~7s measured, more at
     4x), and a button that goes quiet for ten seconds reads as a button that did nothing. */
  if (variant === 'vendor') say('Preparing print-ready artwork…');
  const artwork = variant === 'vendor' ? await uploadDesignArtwork(ctx.state) : null;

  const spec = designSpec(ctx.state, { customerName });
  /* THE COVER'S HEADLINE, SET IN THE BUYER'S OWN TYPEFACE (Ryan 2026-08-27). Rasterised here
     because it cannot be done there: every face in the library is woff2, whose glyphs are brotli-
     compressed, and a Worker has no brotli decompressor — see `ui/heading-image.js`. Customer sheet
     only; the vendor sheet has no cover to put it on. Null simply means the cover sets the heading
     in Helvetica, exactly as it did before. */
  const headingPng = doc_cover(variant) ? await renderHeadingPng(spec.heading, spec.fontId) : null;

  /* ONE ENTRY PER PRODUCT, ONE PAGE EACH (the vendor sheet). `snapshotPng` stays the fallback and
     the ONLY thing the customer sheet sends: it has a cover and one picture, and nobody is tearing
     it apart to hand a page to a different bench. */
  const pages = allProducts ? await vendorSheetPages(ctx) : null;

  // Document-sized, not download-sized — see PDF_SNAPSHOT_PX.
  const blob = pages ? null : await snapshotBlob(ctx, { size: PDF_SNAPSHOT_PX });
  let snapshotPng = blob ? await blobToBase64(blob) : null;
  // One retry smaller, then give up on the IMAGE rather than on the DOCUMENT. The worker builds a
  // spec-led sheet with no plate on it, which is far better than failing a rep's document because
  // a picture wouldn't fit — and the sizes above make this branch unreachable in practice, which is
  // exactly when a guard is worth having.
  if (snapshotPng && snapshotPng.length > PDF_SNAPSHOT_B64_MAX) {
    const smaller = await snapshotBlob(ctx, { size: 900 });
    const b64 = smaller ? await blobToBase64(smaller) : null;
    snapshotPng = b64 && b64.length <= PDF_SNAPSHOT_B64_MAX ? b64 : null;
  }

  /* ⛔ AND THE SAME GUARD ON THE WHOLE SET, because the per-image ladder sizes by COUNT and not by
     what the renders actually weigh. A design with a big photographic logo could put four stills
     over the 2MB body cap even at 900px, and `/wd-pdf` would answer 413 — losing the document
     rather than a picture.
     ⚠ IT DROPS PIXELS, NEVER PAGES. The heaviest entries give up their image first and keep their
     product, so the worst case is a build sheet with a page of spec and no photo for one item —
     never a page missing, and never a failed generate. Sorted heaviest-first so one enormous render
     cannot cost three small ones their images. */
  if (pages) {
    let total = pages.reduce((n, p) => n + (p.png ? p.png.length : 0), 0);
    if (total > PDF_SNAPSHOT_B64_MAX) {
      const order = pages.map((p, i) => i).sort((a, b) => (pages[b].png || '').length - (pages[a].png || '').length);
      for (const i of order) {
        if (total <= PDF_SNAPSHOT_B64_MAX) break;
        total -= (pages[i].png || '').length;
        pages[i].png = '';
      }
    }
  }

  /* ⚠ AN ADMIN-ONLY DOCUMENT HAS TO CARRY THE TOKEN, because the endpoint checks `rep.admin` for
     it. Read through `portalToken()` and not out of sessionStorage: access tokens live thirty
     minutes and this is a long-dwell tool, which is the entire reason `portal-api.js` exists.
       The raw `fetch` stays rather than moving to `portalApi` — this route answers with TWO shapes
     (JSON carrying a URL normally, raw PDF bytes when R2 refused), and `portalApi` treats a
     non-JSON 200 as a failure, which would throw away the document on exactly the branch that
     exists to save it. */
  let bearer = null;
  if (admin) {
    bearer = await portalToken();
    if (!bearer) { say('Your session expired — reload the page to sign in again.'); return false; }
  }

  let res;
  try {
    res = await fetch(PDF_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}) },
      body: JSON.stringify({
        shareId: id,
        snapshotPng,
        ...(pages ? { snapshots: pages } : {}),
        // WHICH document. The worker whitelists this, so a type added to PDF_TYPES without a
        // matching branch there fails loudly instead of quietly returning the wrong sheet.
        variant,
        /* No `zip` any more — the cover stopped printing a location line, so the field that fed
           it was removed from the dialog (2026-08-27). `organization` is the whole contact. */
        /* ⚠ `name` ARRIVES ALREADY ABBREVIATED — "J. Smith", cut in `pdf-sheets.js` so the buyer's
           full name never leaves the browser. Do not pass a raw name through here. */
        quote: { contact: { organization: customerName, ...(contactName ? { name: contactName } : {}) } },
        rep: getRep(),
        /* THE NAMES FOR THINGS THE WORKER CANNOT NAME (2026-08-27) — the products, the
           typeface's real title, and each colour with the job it does. It has no
           `products.js`, no font manifest and no colour model, so every one of these was
           previously a fallback or a raw internal id on the page: "—" for the product,
           "montserrat2" for Montserrat, "Primary"/"Secondary" for the swatches. The design
           ITSELF still comes from KV under `shareId`, so the picture and the words on the
           plate cannot drift from the link; this only supplies the vocabulary. */
        spec,
        ...(headingPng ? { headingPng } : {}),
        // Signed `/files/…` paths; the worker validates the prefix and applies its own origin.
        ...(artwork ? { artwork } : {}),
        /* WHICH DEAL, WHICH DESIGN, WHOSE PO — all three or none (the worker checks). This is what
           makes the sheet findable later; see the note on `po` above. */
        ...(po && po.quoteRef && po.designId && po.poVendor
          ? {
            quoteRef: po.quoteRef,
            designId: po.designId,
            poVendor: po.poVendor,
            /* Renames the sheet after the order it was made for — "Design Details - PO {n} -
               {date}". Optional within the registration: a sheet still registers without it, it
               just keeps the buyer-facing name. */
            ...(po.poNumber ? { poNumber: po.poNumber } : {}),
          }
          : {}),
      }),
    });
  } catch {
    say('Couldn’t reach the PDF service. Check your connection and try again.');
    return false;
  }

  /* TWO SHAPES COME BACK, and the type tells you which. Normally it is JSON carrying a URL —
     the worker stored the sheet and the browser should OPEN it (Ryan 2026-07-29: "I like how in
     the GLD it generates in your browser then you can download from there"). If R2 refused, the
     worker falls back to the bytes themselves rather than lose a document the rep waited for, and
     we download those. A failure is JSON too, so check for the URL, not merely for JSON. */
  const type = res.headers.get('Content-Type') || '';

  if (/json/i.test(type)) {
    let d = null;
    try { d = await res.json(); } catch { /* malformed */ }
    if (d && d.ok && d.url) {
      const href = new URL(d.url, PDF_ENDPOINT).href;
      /* The caller drives what happens to the document. `recorded` is the worker's answer to "did
         the registration stick?" — a sheet that built but did not register is still a document,
         and the DD needs to be able to tell the rep which of those happened rather than reporting
         a success the purchase order will not honour. */
      if (!openResult) {
        ctx.status?.success?.('Design sheet built.');
        return { ok: true, url: href, filename: d.filename || 'design-sheet.pdf', recorded: !!d.recorded };
      }
      /* ⛔ `window.open(url, name, 'noopener')` RETURNS NULL BY SPEC, ALWAYS — that is what the
         feature IS: no handle back to the opened page. So the blocked-popup fallback below fired on
         every single generate and opened the document a SECOND time (Ryan 2026-08-27: *"it opens 2
         new tabs in your browser"*, on both sheets). The first tab was the same PDF still rendering,
         which is why it showed only the cover and felt *"slower and more clunky"* — it was not a
         different document, it was the same one caught mid-paint.
           Opened WITHOUT `noopener` so the return value is meaningful again, and `opener` is severed
         by hand immediately after. Same protection, an answer we can actually branch on. */
      let win = null;
      try { win = window.open(href, '_blank'); } catch { win = null; }
      if (win) { try { win.opener = null; } catch { /* cross-origin by the time it loads — fine */ } }
      if (!win) {
        const a = h('a', { href, download: d.filename || 'design-sheet.pdf', target: '_blank', rel: 'noopener', style: { display: 'none' } });
        document.body.appendChild(a); a.click(); a.remove();
        ctx.status?.info?.('Your browser blocked the preview — the PDF is downloading instead.');
        return true;
      }
      ctx.status?.success?.('Design sheet opened in a new tab.');
      return true;
    }
    const msg = (d && (d.error || d.message)) || '';
    say(msg ? `Couldn’t build the design sheet — ${msg}` : 'Couldn’t build the design sheet.');
    return false;
  }

  if (!res.ok || !/pdf/i.test(type)) {
    say('Couldn’t build the design sheet.');
    return false;
  }

  // Fallback path: raw bytes, because storing failed. The name still follows the convention the
  // worker would have used, so what lands in Downloads reads the same either way.
  const bytes = await res.blob();
  const stem = customerName.trim() || 'Custom design';
  downloadBlob(bytes, `${pdfDocName(variant)} – ${stem} – ${todayISO()}.pdf`);
  ctx.status?.success?.('PDF downloaded.');
  return true;
}

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — ONE LINK THAT PAGES THROUGH THE WHOLE CHAIN.
   ─────────────────────────────────────────────────────────────────────────────────────────────
   Ryan: *"when a deal owner wants to show 3 or 4 or 5 designs of the same thing off to a customer,
   they can... share it as a link which when opened in presentation mode allows them to scroll
   through each design."* Four links in an email is four decisions; one link with arrows is a
   comparison, which is the thing the customer is actually making.

   ⛔ IT REUSES THE SET, NOT A NEW FORMAT. A share already carries several envelopes (`set`) and
   presentation mode already walks them with arrows and the keyboard — and a flip goes through
   `applyEnvelope`, the app's own wholesale restore, so it does not care whether the next slide is a
   different PRODUCT or another VERSION of the same one. All that was missing was a label per slide,
   because every version of one product otherwise prints the same product name.

   ⚠️ `designSet` CANNOT DO THIS and is deliberately left alone: it returns `[]` for a single
   product ("a set of one is just a design"), which is right for a buyer's multi-product share and
   exactly wrong for a chain, where every entry IS that one product.

   ⚠️ THE ENVELOPES ARE FETCHED, ONE PER VERSION. The app holds only the design on screen. Each
   carries its own base64 logo, so three versions is three copies of it — see the 413 note below,
   which is the one failure this cannot paper over. */
/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ONE SHARE OUT OF N ENVELOPES. Factored out
   of `copyChainLink` because three surfaces now need it: Copy link, Email my design and Share via.
   One entry writes a plain single-design share (no `set`), which is what makes "just this version"
   and "all of them" the same code path with a different length.
   ⛔ A 413 IS RETURNED, NOT PAPERED OVER. `storeDesign` retries without the set because for one
   design the set is the extra; here the set IS the point, and a link that silently arrives carrying
   one version is worse than an error because nobody would notice. */
async function mintVersionsShare(ctx, entries) {
  const product = ctx.state?.ui?.product || '';
  const body = {
    design: entries[0].design,
    meta: { orgName: ctx.state?.designProfile?.orgName || '', product, source: 'weight-designer' },
  };
  /* ⛔ THE SET GOES EVEN AT ONE. It used to be added only past two, on the reasoning that a set of
     one is just a design — true of the ARTWORK and false of the LABEL, which is the only place the
     headline above the plate can travel. A single version we made for a customer has a sentence of
     its own to say, and without the set it arrived reading like anybody's design. */
  body.set = entries;

  let res;
  try {
    res = await fetch(SHARE_ENDPOINT, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
  } catch { return { error: 'offline' }; }
  if (res.status === 413) return { error: 'too-large' };
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok || !data || !data.ok || !data.id) return { error: 'refused', message: data && data.message };
  return { id: data.id };
}

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — WHAT THE CUSTOMER READS ABOVE THE PLATE, and
   the four cases are Ryan's own:
     · their own submission          "Your custom weight design"
     · ours, on a chain THEY started "Our custom weight design for you - version B"
     · ours, on a chain WE started   "A custom weight design from Blue Sky Fitness Supply - version B"
     · ours, we started it, alone    "A custom weight design from Blue Sky Fitness Supply"

   ⚠️ THE VERSION LETTER IS THE REF'S OWN LAST SEGMENT (`WEIGHT-XXXX-B` → "B"), not a count and not
   an index into the link. A rep who sends B and D must have them read B and D; numbering them 1 and
   2 would rename the rep's own files in the customer's copy.

   ⚠️ "OURS ON THEIR CHAIN" DOES NOT DEPEND ON THEIR DESIGN BEING IN THE LINK. Ryan: *"if we share a
   link to a design which is a part of a chain that they started ... but we don't include their
   design on the share, the header can still read 'Our custom weight design for you'."* It is a fact
   about where the design came FROM, which is why `adopted` is carried per version rather than
   inferred from what happens to be in the set.

   ⛔ AND THE LAST CASE NEEDS THE COUNT, which is why this runs at mint time and not in the panel:
   a lone design of ours has no other version to be told apart from, so the suffix would be noise. */
/** The two facts a slide needs about its owner, alongside the sentence it shows. */
function slideOwnerFor(v) {
  if (!v) return {};
  const authored = v.authored === 'customer' ? 'customer' : 'rep';
  return Object.assign({ authored }, (authored === 'customer' && v.fromShare) ? { origin: v.fromShare } : {});
}

function slideLabelFor(v, count) {
  if (!v) return '';
  if (v.authored === 'customer') return 'Your custom weight design';
  const rev = String(v.rev || '').trim().toUpperCase();
  if (v.adopted) return 'Our custom weight design for you' + (rev ? ' - version ' + rev : '');
  const house = 'A custom weight design from Blue Sky Fitness Supply';
  return (count > 1 && rev) ? `${house} - version ${rev}` : house;
}

/** A share id as the PUBLIC designer URL — the same builder Copy link has always used. */
function shareUrlFor(id) {
  const base = designerLink();
  return `${base}${base.includes('?') ? '&' : '?'}${SHARE_PARAM}=${encodeURIComponent(id)}`;
}

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — REWRITE A LINK WE ALREADY HANDED OUT, under
   the same id, so the URL in a customer's inbox shows the current versions and the current wording.
   `designId` is the authorisation: the worker checks it is a design this rep may touch which
   records this share. */
async function updateShare(ctx, { shareId, designId, design, set }) {
  try {
    const r = await portalApi('/wd-share-update', { method: 'POST', body: { shareId, designId, design, set } });
    return !!(r && r.ok);
  } catch { return false; }
}

/** Stamp a chain's share link onto every rung, so whichever one is linked to a deal carries it. */
async function stampChain(versions, shareId) {
  for (const v of (versions || [])) {
    if (!v || !v.id) continue;
    try { await portalApi('/wd-design-chain-share', { method: 'POST', body: { designId: v.id, shareId } }); }
    catch { /* the link is in the rep's hands regardless */ }
  }
}

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ ONE LINK PER CHAIN, FOR THE LIFE OF THE
   CHAIN. Ryan: *"do the permanently live chain link too."* Saving a version used to mint a brand new
   share and record that instead, which stranded every link already sent at the versions it held on
   the day it was copied. Now the id is found once and the record behind it is rewritten, so a link
   a customer received last week gains this week's version.

   ⚠️ IT MINTS ONLY WHEN THERE IS NOTHING TO UPDATE — a chain that has never been shared, or one
   whose record has expired out of KV (two years) and can no longer be written under its own id.
   Falling back to a mint there is right: a dead link is worth replacing.

   ⛔ AND IT STAMPS EVERY RUNG AFTERWARDS, including when it updated rather than minted. A version
   saved today has no `chainShareId` on it yet, and the stamp is how the deal's linked file — which
   may well BE that new version — comes to carry the link the snippets read. */
/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ THE NEWEST OF OURS ALWAYS HAS ITS OWN LINK.
   "Link to the version we made for them" is gated on `soloShareId`, and that was written only by a
   version SAVE — so a rep whose version predates the field had a greyed row and no way to ungrey it
   short of saving a version they did not want. Ryan hit exactly that, twice.

   The newest of ours is the version that snippet sends, so it is the one that must have a link. This
   runs when the share dialog opens, which is the moment we start advertising links, and it is
   update-or-mint: a version that already has one keeps its URL and has its contents refreshed.

   ⚠️ ONE RUNG, NOT ALL OF THEM. Minting a share for every version on every open would cost a KV
   write per rung for links nothing sends. */
export async function ensureNewestSoloLink(ctx, versions) {
  const ours = (versions || []).filter((v) => v && v.authored !== 'customer' && v.design);
  const newest = ours[ours.length - 1];
  if (!newest) return '';
  return syncSoloShare(ctx, versions, newest.id);
}

export async function syncChainShare(ctx, versions, opts) {
  const mintIfMissing = !(opts && opts.mint === false);
  const usable = (versions || []).filter((v) => v && v.design);
  if (usable.length < 2) return '';
  const product = ctx.state?.ui?.product || '';
  const entries = usable.map((v) => Object.assign(
    { product, design: v.design, label: slideLabelFor(v, usable.length) }, slideOwnerFor(v),
  ));

  const holder = usable.find((v) => v.chainShareId);
  if (holder) {
    const ok = await updateShare(ctx, {
      shareId: holder.chainShareId, designId: holder.id, design: entries[0].design, set: entries,
    });
    if (ok) { await stampChain(usable, holder.chainShareId); return holder.chainShareId; }
  }
  /* ⛔ A PLAIN SAVE REFRESHES, IT DOES NOT MINT. Ryan tweaked `WEIGHT-1014-C` and saved over it,
     then opened the snippet link and got the design as it stood before the tweak. The links were
     only ever re-synced on a save-as-new-VERSION, so any ordinary edit to a saved design left every
     link already sent pointing at the previous drawing — silently, and with no way to tell.
       Minting is still gated to a version save, though: a rep saving a design nobody has shared
     should not have a public URL created for it as a side effect of pressing Save. */
  if (!mintIfMissing) return '';
  const minted = await mintVersionsShare(ctx, entries);
  if (!minted || !minted.id) return '';
  await stampChain(usable, minted.id);
  return minted.id;
}

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ ASK BEFORE SENDING, WHEN THERE IS SOMETHING
   TO ASK ABOUT. Ryan: *"when you click 'Copy link' a little thing can appear asking you to confirm
   the checkboxes should you have a file open which has more than one version saved in its chain...
   perhaps then we don't need the checkboxes on the save menu"* and *"Same thing should theoretically
   happen when someone clicks on the 'Email my design' button or the 'Share via...' button too."*

   This replaces the ticks in YOUR DESIGNS and the separate "Copy link to all versions" row. Both
   were the same decision expressed twice, in a different panel from the button that acted on it —
   which is exactly how ticking two designs and pressing Copy link came to copy one.

   ⚠️ IT ONLY APPEARS WHEN IT HAS A QUESTION: rep mode, and a chain with two or more versions. A
   buyer, and a rep with a single design, get the plain share they always got, with no dialog.

   ⛔ THE LINK IS MINTED WHILE THE DIALOG IS OPEN, not after the button is pressed. `navigator.share`
   needs transient activation and an awaited fetch spends it — the whole reason `warmShareLink`
   exists. Minting on open and on every tick change means the confirm click has a URL already in
   hand. If a mint is still in flight we wait for it and accept the risk, which `shareViaSystem`
   already recovers from by copying to the clipboard.

   @returns {Promise<null|{cancelled:true}|{url:string}|{failed:true,message:string}>}
     `null` means "no question to ask" — the caller takes its ordinary path. */
async function pickShareUrl(ctx, actionLabel) {
  if (!isRepMode()) return null;
  let versions = [];
  try { versions = (await ctx.chainVersions?.()) || []; } catch { versions = []; }
  if (versions.length < 2) return null;

  const product = ctx.state?.ui?.product || '';
  const chosen = new Set(versions.map((v) => v.id));
  const keyOf = () => versions.filter((v) => chosen.has(v.id)).map((v) => v.id).join(',');
  /* The chain's own permanent link, if it has one. Sending the whole chain REUSES it — minting a
     rival id here would leave two links for one chain and only one of them would stay current. */
  const holder = versions.find((v) => v && v.chainShareId) || null;

  let want = '';                       // the key the newest mint was asked for
  let got = { key: '', url: '', error: '' };
  let inflight = null;

  const mint = () => {
    const key = keyOf();
    if (!key || got.key === key || want === key) return;
    want = key;
    const _picked = versions.filter((v) => chosen.has(v.id));
    const entries = _picked.map((v) => Object.assign(
      { product, design: v.design, label: slideLabelFor(v, _picked.length) }, slideOwnerFor(v),
    ));

    /* ⛔ THE WHOLE CHAIN GOES DOWN ITS EXISTING LINK. The URL is known without a round trip, so the
       confirm click never waits on the network — which is what keeps `navigator.share` inside its
       activation. The rewrite rides along in the background; the record it replaces is the same
       chain, so a viewer arriving mid-write sees the previous version of the same link rather than
       anything wrong. */
    if (holder && _picked.length === versions.length) {
      got = { key, url: shareUrlFor(holder.chainShareId), id: holder.chainShareId, stable: true, error: '' };
      inflight = updateShare(ctx, {
        shareId: holder.chainShareId, designId: holder.id, design: entries[0].design, set: entries,
      }).then(() => got.url);
      return;
    }

    inflight = mintVersionsShare(ctx, entries).then((r) => {
      /* ⚠️ THE SELECTION CAN MOVE WHILE A MINT IS IN FLIGHT. Publishing an older result would hand
         the rep a link for boxes they have since unticked — silently, and with the right count. */
      if (want !== key) return '';
      got = r && r.id
        ? { key, url: shareUrlFor(r.id), error: '', id: r.id }
        : { key, url: '', error: (r && r.error) || 'refused', message: r && r.message };
      return got.url;
    });
  };

  const primary = { id: 'go', label: actionLabel, variant: 'primary' };
  const rows = versions.map((v) => h('label', { class: 'wd-vp-row' },
    h('input', {
      type: 'checkbox', class: 'wd-vp-tick', checked: true,
      onChange: (e) => {
        if (e.currentTarget.checked) chosen.add(v.id); else chosen.delete(v.id);
        mint();
      },
    }),
    /* The REP's words, not the customer's — see the note in designs-menu's `chainVersions`. */
    h('span', { class: 'wd-vp-label' }, v.pickLabel || '')));

  const bodyEl = h('div', { class: 'wd-vp' },
    h('p', { class: 'wd-vp-note' },
      `This design has ${versions.length} versions. Untick any you do not want to send.`),
    ...rows,
    h('p', { class: 'wd-vp-foot' }, 'One link. The arrows page through whatever you leave ticked.'));

  mint();
  /* Fire and forget: the rep is about to send a link, and the row that offers OUR version must not
     be greyed when they get to the email. It cannot block the dialog — a share write is not worth a
     spinner in front of a list of checkboxes. */
  void ensureNewestSoloLink(ctx, versions);
  const go = await openModal({
    mount: ctx.el,
    title: 'Which versions do you want to send?',
    body: bodyEl,
    actions: [primary, { id: 'cancel', label: 'Cancel', variant: 'ghost' }],
  });
  if (go !== 'go') return { cancelled: true };
  if (!chosen.size) return { cancelled: true };

  if (got.key !== keyOf()) { try { await inflight; } catch { /* handled below */ } }
  if (got.url && got.key === keyOf()) {
    /* Recorded only when the whole chain went, because that link is what the email snippets send
       when nobody has chosen anything. A hand-picked pair must not narrow it. */
    if (chosen.size === versions.length && got.id) await stampChain(versions, got.id);
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ AND ONE VERSION OF OURS IS THAT
       DESIGN'S OWN LINK. Ryan found "Link to the version we made for them" still greyed: that
       snippet is gated on `soloShareId`, which until now was written only by a version SAVE. So a
       rep could share exactly the thing the snippet offers and the snippet would still say there
       was nothing to offer. Sending one design IS minting its solo link, so record it.
       ⚠️ ONLY OURS. The customer's own rung is not a design we made for them, and stamping it as one
       would put their artwork behind a sentence that says we drew it. */
    if (chosen.size === 1 && got.id) {
      const one = versions.find((v) => v && chosen.has(v.id));
      if (one && one.authored !== 'customer') {
        try { await portalApi('/wd-design-chain-share', { method: 'POST', body: { designId: one.id, shareId: got.id, solo: true } }); }
        catch { /* the link is in the rep's hands regardless */ }
      }
    }
    return { url: got.url };
  }
  return {
    failed: true,
    message: got.error === 'too-large'
      ? 'These versions are too large to share on one link, because each carries its own copy of the logo. Untick one and try again.'
      : (got.message || 'Couldn’t build a link to these versions.'),
  };
}

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) - `copyChainLink` REMOVED. It was the second
   place that minted a multi-version share and stamped it onto a chain, and its last caller (the
   automatic record on a version save) now goes through `syncChainShare`, which updates the chain's
   existing link instead of minting a rival one. Leaving a dormant minting path behind is how two
   links for one chain would have come back later, with only one of them staying current. */

/**
 * Open the buyer's mail client with a note about their design.
 * Async now: the link in the body is a REAL share link, so the design has to be stored before
 * the mail window opens. Falls back to the bare designer URL if storing fails — an email that
 * points at the tool is still worth sending, and it beats blocking on a network hiccup.
 */
/**
 * Who the design is FOR, for the email subject — "Battery Creek HS Dolphins" (Ryan 2026-07-29).
 *
 * `orgName` / `mascot` are the primary source because they are the identity's real home: the Add
 * Logo tab's persistent fields, which survive a logo swap, a resume AND a trip through a layout with
 * no arc slots (see `reseedIdentityIntoPlaceholders`). The arc TEXT is downstream of them.
 *
 * But it is only downstream in one direction — a buyer can type straight into the Wording override
 * fields without ever filling the identity pair, and then the name is on the plate while `orgName` is
 * empty. So the arc text is the fallback, filtered two ways: `textUserSet` (the app's own derived
 * lines would be circular) and `isPlaceholderText` (a subject line reading "YOUR TEXT GOES HERE"
 * would be worse than no name at all). Deduped, because the twin-arch layouts paint the same words in
 * both slots.
 *
 * @returns {string} '' when the design has no identity yet — the caller drops the segment.
 */
function designIdentityName(ctx) {
  const p = (ctx && ctx.state && ctx.state.designProfile) || {};
  const pair = [String(p.orgName || '').trim(), String(p.mascot || '').trim()].filter(Boolean);
  if (pair.length) return [...new Set(pair)].join(' ');
  const typed = (p.elements || [])
    .filter((e) => e && e.type === 'arctext' && e.textUserSet === true && !isPlaceholderText(e.text))
    .map((e) => String(e.text || '').trim())
    .filter(Boolean);
  return [...new Set(typed)].join(' ');
}

/**
 * Hand the buyer a composed email they can send without editing it first.
 *
 * ⚠ WHAT THIS IS, because the label understates it: a `mailto:`, so the buyer's own mail client
 * opens with the words already in it and THEY press send. Which means the only thing this function
 * controls is PLAIN TEXT. Font, size and paragraph spacing belong to the mail client and cannot be
 * set from here at all — there is no HTML in a mailto body (RFC 6068). Ryan asked for Calibri 11 and
 * that is his Outlook default already; what he can't get this way is control of it. If we ever need
 * to OWN the typography, the email has to be sent server-side as real HTML (rfq-worker already has
 * Resend wired for `/weight-designer-notify`), which costs an address field the tool doesn't collect.
 *
 * TWO THINGS FIXED HERE (Ryan 2026-07-29 — "seems like we rushed it a bit"):
 *
 * 1. THE VOICE WAS WRONG, and this is why the old text read as something you'd tidy before sending.
 *    The buyer is the sender — they are showing a design to a coach or a colleague — but the copy
 *    spoke as Blue Sky ("we review your artwork with you"), so the sender was ventriloquising the
 *    shop. ⚠ THE TWO LINES BELOW ARE RYAN'S OWN WORDS, given verbatim (2026-07-29) — don't "improve"
 *    them. Note the link sits INLINE at the end of the first sentence rather than on its own line,
 *    which is also what keeps the whole body to two paragraphs.
 *
 * 2. NO BLANK LINES BETWEEN BLOCKS. Ryan: *"the text is formatted to be those extra tall lines where
 *    you have the text towards the top of the line, then a blank space below which is still part of
 *    the same line… I hate this style."* That space is the mail client's paragraph spacing — Outlook
 *    turns every newline in a mailto body into its own paragraph and puts space AFTER it. So a `''`
 *    entry in this list does not add a line break, it adds a second empty PARAGRAPH on top of the
 *    spacing that was already there, which is what doubles the height. Single newlines only: the
 *    client's own spacing is the break, and nothing stacks on top of it. Same principle as
 *    `abby-body-spacing-blank-lines-not-margin`, in reverse — there the gaps had to be real lines
 *    because nothing else supplied them; here something already does.
 */
export async function emailDesign(ctx) {
  // Reads the warm cache, so opening the Share card and then tapping Email is instant instead of
  // POSTing the design a second time. Falls back to the bare designer link exactly as before when
  // storage refuses — this route can still be useful with no design attached, unlike Copy link.
  /* The same question Copy link asks, for the same reason: with a chain open, "my design" is
     ambiguous and the answer belongs to the rep, not to a default. Cancelling opens no mail. */
  const picked = await pickShareUrl(ctx, 'Open email');
  if (picked && picked.cancelled) return;
  if (picked && picked.failed) { ctx.status?.info?.(picked.message); return; }
  const link = (picked && picked.url) || (await warmShareLink(ctx)) || designerLink();
  wdTrack(WD_EVENTS.SHARED); // fired here now, not in storeDesign — see the note there
  // `3D Weight Design - [Organization / Mascot Name if it exists] - Blue Sky Fitness Supply`
  // (Ryan 2026-07-29, his format verbatim). "if it exists" is the whole spec: the middle segment is
  // DROPPED rather than left blank, so a design with no identity yet reads
  // "3D Weight Design - Blue Sky Fitness Supply" and never "3D Weight Design -  - Blue Sky…".
  const subject = encodeURIComponent(
    ['3D Weight Design', designIdentityName(ctx), 'Blue Sky Fitness Supply'].filter(Boolean).join(' - '),
  );
  // Ryan's wording, verbatim. No blank entries — see (2) above.
  const bodyLines = [
    `Check out my custom 3D weight design on the Blue Sky Fitness Supply Weight Designer: ${link}`,
    'Feel free to edit the design using the link above.',
  ];
  const body = encodeURIComponent(bodyLines.join('\n'));
  try { window.location.href = `mailto:?subject=${subject}&body=${body}`; } catch { /* no mail client */ }
}

/**
 * Say something transient ON an action button ("Saving…", "Copied ✓") without wrecking it.
 *
 * ⛔ NEVER `btn.textContent = …` ON THESE BUTTONS (Ryan 2026-08-16: *"after it's clicked the entire
 * look/feel of the button changes — font/words/font size/font colour"*).
 *   An action row is a STRUCTURE, not a text node: `<button><span icon><span text><span label>
 * <span sub>`. Every bit of its typography — size, weight, colour, the muted sub line — is styled on
 * `.wd-ca-row__label` / `__sub`, not on the button. Writing `textContent` replaces that whole subtree
 * with one bare text node, so the row loses its icon and falls back to the button's own inherited
 * font. And because the old code read `prev` the same way, the "restore" put back the CONCATENATION
 * ("Copy linkSend your design to the committee") as a single unstyled run — the change was permanent,
 * not just for the 1.4s.
 *
 * So write the LABEL element and leave the rest of the row standing. Two further rules fall out of
 * a button that can be pressed again while a message is still showing:
 *   · the pristine label is stashed on the element the FIRST time it is touched, so a second click
 *     cannot bake "Copied ✓" in as the thing to restore to;
 *   · a pending restore is cancelled when a new message arrives, so the first timer cannot fire mid
 *     second message and put the row back early.
 *
 * @param {HTMLElement|null} btn
 * @returns {{say: (text: string, holdMs?: number) => void}} a no-op when there is no button.
 */
function transientLabel(btn) {
  const el = btn ? (btn.querySelector('.wd-ca-row__label, .wd-share-row__label') || btn) : null;
  if (!el) return { say: () => {} };
  if (el.dataset.wdLabel === undefined) el.dataset.wdLabel = el.textContent || '';
  return {
    say(text, holdMs) {
      window.clearTimeout(Number(el.dataset.wdLabelTimer) || 0);
      el.textContent = text === null ? (el.dataset.wdLabel || '') : text;
      if (holdMs) {
        el.dataset.wdLabelTimer = String(window.setTimeout(() => {
          el.textContent = el.dataset.wdLabel || '';
        }, holdMs));
      }
    },
  };
}

/**
 * Share a link we ALREADY HAVE — system sheet on a phone, clipboard everywhere else.
 *
 * ⛔ TAKES THE URL RATHER THAN MINTING ONE, which is the whole reason this exists beside
 * `copyDesignerLink`. That one calls `warmShareLink` and POSTs a fresh share record; the quote
 * hand-off has already stored the design and the lead is carrying that exact link to the rep. A
 * second record would be a divergent copy of the same design — the buyer would be showing their
 * committee one URL while the rep opens another, and only one of them is attached to the deal.
 *
 * Used by the sent card (ui/product-step.js), where the buyer has just submitted and the useful
 * verb is "show this to someone", not "view it again" — they are looking straight at it.
 *
 * @param {object} ctx
 * @param {string} url   an existing `?d=` link
 * @param {HTMLElement} [btn]  gets a transient "Copied ✓" label
 */
export async function shareExistingLink(ctx, url, btn) {
  if (!url) return false;
  const label = transientLabel(btn);

  if (canWebShare()) {
    try {
      await navigator.share({ title: designShareTitle(ctx), text: SHARE_TEXT, url });
      wdTrack(WD_EVENTS.SHARED);
      return true;
    } catch (err) {
      // AbortError = they opened the sheet and dismissed it. That is not a failure and must not
      // fall through to a clipboard write they did not ask for.
      if (err && err.name === 'AbortError') return false;
      /* Anything else (a browser that advertises navigator.share and refuses non-https, an OS with
         no targets) falls through to the copy below, which always works. */
    }
  }

  const ok = await copyText(url);
  label.say(ok ? 'Copied ✓' : 'Copy failed', 1400);
  if (ok) { wdTrack(WD_EVENTS.SHARED); ctx.status?.success?.('Link copied — it opens your design.'); }
  else ctx.status?.info?.(url);   // clipboard denied → show it so it can be copied by hand
  return ok;
}

/**
 * Copy a share link to the clipboard — one that actually carries the design.
 *
 * It used to copy the page URL, which meant "Send your design to the committee" sent them an
 * empty designer. Storing first makes the button honest, at the cost of a round trip, so the
 * label narrates it: a multi-MB logo takes a moment and a silent button reads as broken.
 */
export async function copyDesignerLink(ctx, btn) {
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ THE TICKS STEER THIS BUTTON. Ryan:
     *"I thought it'd be a case of I check the boxes for all the designs in the stack I want, then
     click 'Copy link' and it'll create/copy a presentation link to just those version(s)."* He is
     describing the only reading the checkboxes support: a tick is an instruction about what to
     send, and the button next to it that says "Copy link" is what sends it. Copying one design
     while two are ticked is the app disagreeing with its own controls.
       ⚠️ AT TWO OR MORE, NOT AT "SUBSET". Ticking both of two is the ordinary case and is not a
     subset of anything; gating on subset left it copying a single design. Below two this is the
     plain single-design share it has always been, for reps and buyers alike. */
  const picked = await pickShareUrl(ctx, 'Copy link');
  if (picked && picked.cancelled) return;
  if (picked && picked.failed) { ctx.status?.info?.(picked.message); return; }
  if (picked && picked.url) {
    const okPick = await copyText(picked.url);
    if (btn) { const t = btn.textContent; btn.textContent = okPick ? 'Copied' : 'Copy failed'; setTimeout(() => { btn.textContent = t; }, 1500); }
    if (okPick) { wdTrack(WD_EVENTS.SHARED); ctx.status?.success?.('Link copied. It opens on the first version and pages through the rest.'); }
    else ctx.status?.info?.(picked.url);
    return;
  }
  const label = transientLabel(btn);
  label.say('Saving…');
  if (btn) btn.disabled = true;
  const link = await warmShareLink(ctx); // warm cache — no second POST if the card was already open
  if (btn) btn.disabled = false;
  // No link → do NOT quietly copy the bare designer URL. That is the old bug wearing a new
  // hat: the buyer would believe they had sent their design. `createShareLink` has already
  // surfaced the worker's reason, so say plainly that this route is unavailable.
  if (!link) {
    label.say('Couldn’t save', 1800);
    ctx.status?.info?.('Couldn’t create a share link. Try “Download image” or “Email me my design”.');
    return;
  }
  const ok = await copyText(link);
  label.say(ok ? 'Copied ✓' : 'Copy failed', 1400);
  if (ok) { wdTrack(WD_EVENTS.SHARED); ctx.status?.success?.('Link copied — it opens your design.'); }
  else ctx.status?.info?.(link); // clipboard denied → at least show it so it can be copied by hand
}

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — THIS DESIGN'S OWN PUBLIC LINK, recorded on
   its record so the second email snippet can find it. Minted from the LIVE STAGE, which is the
   version that was just saved — the same envelope the save itself sent.
   ⛔ NOT the chain link. That one opens on the customer's design and pages through everything,
   which is the third snippet; this one is ours alone, which is what the second snippet's sentence
   promises. Best effort: a design that is saved is saved whether or not this lands. */
export async function syncSoloShare(ctx, versions, designId, opts) {
  const mintIfMissing = !(opts && opts.mint === false);
  if (!designId) return '';
  const v = (versions || []).find((x) => x && x.id === designId) || null;
  /* The STORED envelope when we have it - it is what the save just wrote - and the live stage
     otherwise, which is the same bytes on the only path that reaches here. */
  const envelope = (v && v.design) || ctx.shareEnvelope?.();
  if (!envelope) return '';
  const product = ctx.state?.ui?.product || '';
  /* A design with no chain still has a headline. Its facts come off the open design's own ref:
     `authored` is ours by definition (a customer's design is never the one a rep saves over) and
     `adopted` is false, which is exactly the house wording. */
  const facts = v || {
    authored: 'rep',
    rev: String(ctx.currentDesign?.()?.ref || '').split('-').pop() || '',
    adopted: false,
  };
  const entries = [Object.assign({ product, design: envelope, label: slideLabelFor(facts, 1) }, slideOwnerFor(facts))];

  let existing = (v && v.soloShareId) || '';
  if (!existing && !v) {
    /* Off-chain, so nothing has told us whether this design has a link. One GET, on a save. */
    try {
      const r = await portalApi(`/wd-design/${designId}`);
      existing = (r && r.ok && r.design && r.design.soloShareId) || '';
    } catch { existing = ''; }
  }
  if (existing) {
    const ok = await updateShare(ctx, { shareId: existing, designId, design: envelope, set: entries });
    if (ok) return existing;
  }
  /* See the note in `syncChainShare`: refresh always, mint only on a version save. */
  if (!mintIfMissing) return '';
  const minted = await mintVersionsShare(ctx, entries);
  if (!minted || !minted.id) return '';
  try { await portalApi('/wd-design-chain-share', { method: 'POST', body: { designId, shareId: minted.id, solo: true } }); }
  catch { /* the share exists; only the record of it failed */ }
  return minted.id;
}

/* A tick in a circle. Distinct from Copy link's chain glyph and from Download's arrow, because this
   row does a third thing: it commits. */
export const ICON_SAVE_BACK =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" '
  + 'stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12.5l2.5 2.5L16 9.5"/></svg>';

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ THE BUYER SAVES THEIR OWN DESIGN BACK, and
   the rep sees it on the link they already have. Ryan: *"if they edit it, then they can email the
   rep and be like 'I just made some edits to my design' and if the rep opens their original design,
   they'll see it updated."*

   ⛔ IT WRITES TO `sharedOrigin`, WHICH IS THEIR SUBMISSION — never the combined link they may have
   arrived on. The Deal Dashboard's customer card reads the submission; a save that landed on the
   combined record would be a save the rep never sees, which is the whole feature failing quietly.
   `leave()` in ui/present.js is what sets and CLEARS that value, so this row cannot exist on a
   design of ours.

   ⚠️ NO EMAIL FIRES. Ryan's flow is that they tell their rep, and an automatic message from a public
   page to a rep's inbox is a different feature with a different blast radius. */
export async function reviseMyDesign(ctx, btn) {
  const shareId = ctx.state?.ui?.sharedOrigin || '';
  if (!shareId) return false;
  const design = ctx.shareEnvelope?.();
  if (!design) { ctx.status?.info?.('There is nothing to save yet.'); return false; }

  const label = transientLabel(btn);
  label.say('Saving…');
  if (btn) btn.disabled = true;
  let res = null;
  try {
    res = await fetch(`${SHARE_ENDPOINT}-revise`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shareId, design, product: ctx.state?.ui?.product || '' }),
    });
  } catch { res = null; }
  if (btn) btn.disabled = false;

  let data = null;
  try { data = res ? await res.json() : null; } catch { /* non-JSON */ }
  if (!res || !res.ok || !data || !data.ok) {
    label.say('Not saved', 1800);
    /* Says what to DO. A buyer who cannot save should not be left wondering whether their work is
       gone — it is still on screen, and the link they were sent still opens it. */
    ctx.status?.info?.((data && data.message)
      || 'Could not save your changes just now. Your design is still on screen, so try again in a moment.');
    return false;
  }
  label.say('Saved ✓', 1600);
  wdTrack(WD_EVENTS.SHARED);
  ctx.status?.success?.('Saved. Let your rep know and they will see the update on your design.');
  return true;
}

/**
 * Fetch a shared design by id. Used by main.js's boot path when the URL carries `?d=`.
 *
 * @returns {Promise<{design: string, set: Array<{product: string, design: string}>, ordered: Array}|null>}
 *   `design` is the autosave envelope the sender had on the stage; `set` is every model the link
 *   carries, **in catalogue order** (see `orderSharedSet`), and is `[]` for a single-product share
 *   or any link written before sets existed. Callers treat "no set" and "a set of one" identically
 *   — there is nothing to migrate.
 *   `ordered` is what the buyer actually asked to be quoted — `[{product, unit, lines:[{weight,qty}]}]`
 *   — and is `[]` on any link that did not come from a quote submission. It is the ONLY thing on the
 *   record that knows the difference between the plate a design was drawn on and the plates it was
 *   drawn FOR; see `orderedDenomination`.
 */
export async function fetchSharedDesign(id) {
  if (!/^[A-Za-z0-9]{8}$/.test(String(id || ''))) return null;
  try {
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ ALWAYS ASK, AND ASK CHEAPLY. Fixing
       the server header (WD 399) stops any NEW stale copy being stored, but it cannot reach the
       ones already sitting in a customer's browser: those were stored as `immutable`, which is
       precisely a promise not to revalidate, and a normal reload will not shift them. The only
       people who could clear those are the people least likely to know how — Ryan: *"most folks
       don't even know what [a hard refresh] is or how to do one."*

       `cache: 'no-cache'` on the REQUEST overrides that. It forces a conditional request every
       time regardless of what the stored entry claims, so a browser holding a copy from before
       today corrects itself on the next open with nobody doing anything.

       ⚠️ NOT `no-store`. With the ETag now on the response, an unchanged share answers 304 with no
       body — so this costs a round trip and not a multi-MB download. `no-store` would throw the
       copy away and re-download the envelope every single time. */
    const res = await fetch(`${SHARE_ENDPOINT}/${encodeURIComponent(id)}`, { cache: 'no-cache' });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || !data.ok || typeof data.design !== 'string') return null;
    // `design` is optional — an entry with only a product is a model the sender picked but never
    // opened, and the recipient's app renders the shared design on it. See `designSet`.
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ `label` SURVIVES THE READ. This mapper
       rebuilt every entry from two named fields, so the slide label the worker stores and the
       presentation header reads was thrown away in between, on the client, silently. Nothing
       errored and no test could see it: the write path was right, the read path was right, and the
       whitelist between them dropped the field.

       Which means the per-slide label shipped in WD 377 has never once reached the screen, and
       every reworking of the wording since (WD 383, 385, 387) was tuning a string that could not
       arrive. Ryan, on a two-version link: *"the header read 'Custom design from Blue Sky Fitness
       Supply' only."* It always would have.

       ⚠️ THE SAME WHITELIST SHAPE the worker's own set-rebuild carries, and WD 377 wrote the warning
       for it there — *"a `label` the client sent would have been silently dropped"* — while the
       mirror of it two files away went unlooked-at. A field added to a record needs the WHOLE round
       trip walked, not the end it was added at. */
    const set = Array.isArray(data.set)
      ? data.set
        .filter((m) => m && typeof m.product === 'string' && m.product)
        /* ⛔ EVERY FIELD THE RECORD CARRIES, not the two this mapper was born with. `label` was
           dropped here for two days and nothing could see it; `authored` decides whether a customer
           may edit a slide and `origin` is the record their edit writes to, so losing either would
           fail open — a buyer editing our version, or a save that lands nowhere. */
        .map((m) => Object.assign(
          { product: m.product },
          typeof m.design === 'string' ? { design: m.design } : {},
          typeof m.label === 'string' && m.label ? { label: m.label } : {},
          typeof m.authored === 'string' && m.authored ? { authored: m.authored } : {},
          typeof m.origin === 'string' && m.origin ? { origin: m.origin } : {},
        ))
      : [];
    /* ⭐ WHAT THEY ORDERED, WHICH IS NOT WHAT THEY DESIGNED ON (Ryan 2026-08-30) — written by the
       QUOTE handoff only, so it is absent on every plain share, on every link older than
       2026-08-30, and on any record whose quote carried no counts. `orderedDenomination` treats
       absent and empty identically, so nothing needs migrating and no caller needs to check. */
    const ordered = Array.isArray(data.ordered)
      ? data.ordered
        .filter((g) => g && typeof g.product === 'string' && g.product && Array.isArray(g.lines))
        .map((g) => ({
          product: g.product,
          unit: g.unit === 'pair' ? 'pair' : 'each',
          lines: g.lines
            .map((l) => ({ weight: Number(l && l.weight), qty: Number(l && l.qty) }))
            .filter((l) => Number.isFinite(l.weight) && l.weight > 0 && Number.isFinite(l.qty) && l.qty > 0),
        }))
        .filter((g) => g.lines.length)
      : [];
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-10-28) — WHO TO CALL, for the presentation's
       contact line. Only a link a rep tied to a saved design carries one; named fields only, the
       same whitelist rule as `set` above. */
    const c = data.contact && typeof data.contact === 'object' ? data.contact : null;
    const contact = c && typeof c.name === 'string' && c.name.trim()
      ? Object.assign(
        { name: c.name.trim() },
        typeof c.phone === 'string' && c.phone.trim() ? { phone: c.phone.trim() } : {},
        typeof c.email === 'string' && c.email.trim() ? { email: c.email.trim() } : {},
      )
      : null;
    return { design: data.design, set: orderSharedSet(set), ordered, contact };
  } catch {
    return null;
  }
}

/**
 * Put a shared set into the order a recipient should walk it (Ryan 2026-08-16: *"the order of the
 * weights in presentation mode should be the order you'd find them in in the pick weights tab"*).
 *
 * ⭐ ORDERED ON ARRIVAL, NOT ON DISPLAY, so there is exactly one writer and every reader — the
 * presentation's arrows, its dots, and the boot path deciding which model the link OPENS on —
 * cannot disagree about which model is first. `fetchSharedDesign` is the only place a set enters
 * the app, which makes it the only place this has to be right.
 *
 * ⚠ THE SENDER ALREADY SORTS, AND THIS IS STILL NOT REDUNDANT. `designSet` builds the set from
 * `pickedProducts()`, whose comparator is category → kind → weight → instance — correct, and it is
 * where the smallest-to-largest half of Ryan's rule comes from. But that order is baked into a
 * record with a **two-year TTL**, so it is only ever as right as the app that wrote it: links
 * written before the set existed, links whose sender fell through `designSet`'s
 * capture-order fallback (no picks), and links from any future app that gets it wrong all keep
 * presenting for two years. The recipient owns what the recipient sees.
 *
 * ⛔ STABLE, AND THAT IS WHAT CARRIES THE WEIGHTS. `catalogueRank` deliberately ties every
 * configuration of one kind together (it has no state to look rungs up in — see its note), so the
 * lightest-first order among them survives only because a tie is left exactly as it was found. The
 * index tie-break makes that explicit rather than leaning on `Array.sort`'s stability guarantee.
 */
export function orderSharedSet(set) {
  return (Array.isArray(set) ? set : [])
    .map((entry, i) => ({ entry, i }))
    .sort((a, b) => (catalogueRank(a.entry.product) - catalogueRank(b.entry.product)) || (a.i - b.i))
    .map((x) => x.entry);
}

// ── §5.7 Save & share sheet ──────────────────────────────────────────────────
export const ICON_DL = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12M12 15l-4-4M12 15l4-4"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/></svg>';
export const ICON_MAIL = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M4 7l8 6 8-6"/></svg>';
export const ICON_LINK = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M9 15l6-6"/><path d="M10.5 6.5l1-1a3.5 3.5 0 0 1 5 5l-1 1"/><path d="M13.5 17.5l-1 1a3.5 3.5 0 0 1-5-5l1-1"/></svg>';
// The OS-share glyph — a box with an arrow leaving it, which is what both iOS and Android use for
// "hand this to another app". Deliberately NOT the three-connected-nodes mark: that one is already the
// canvas cluster's own Share BUTTON (canvas-actions.js `ICON_SHARE`), and this row lives inside the
// card that button opens, so repeating it would read as the same control twice.
export const ICON_SHARE_VIA = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v11"/><path d="M8.5 6.5L12 3l3.5 3.5"/><path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6"/></svg>';

// One action TILE. Modelled on the Gym Layout Designer's share actions (Ryan 2026-07-25): equal-width
// buttons side by side with the icon above a centred label, rather than a stack of full-width rows.
// Wears `.wd-flat-glass` — the tool's default for a button with nothing showing through it — so the
// sheet matches the rest of the designer's chrome instead of using its own flat panel.
function shareRow(icon, label, sub, onClick) {
  const btn = h('button', { type: 'button', class: 'wd-share-row wd-flat-glass', onClick: (e) => onClick(e, btn) },
    h('span', { class: 'wd-share-row__icon', 'aria-hidden': 'true', html: icon }),
    h('span', { class: 'wd-share-row__text' }, h('span', { class: 'wd-share-row__label' }, label), sub ? h('span', { class: 'wd-share-row__sub' }, sub) : null),
  );
  return btn;
}

/** The combined Save & share sheet (§5.7). SUPERSEDED 2026-07-25 by the canvas Save · Share cluster
 *  (ui/shell/canvas-actions.js), which splits these four actions across two slide-down cards. Kept —
 *  unreferenced — because it is the only place the four are described together, and the quote flow
 *  may still want a single sheet. Delete it if that never materialises. */
export function openShareSheet(ctx) {
  const body = h('div', { class: 'wd-share-sheet' },
    ...(canWebShare() ? [shareRow(ICON_SHARE_VIA, 'Share via…', 'Email, WhatsApp, text, AirDrop', () => shareViaSystem(ctx))] : []),
    shareRow(ICON_LINK, 'Copy link', 'Send your design to the committee', (_e, btn) => copyDesignerLink(ctx, btn)),
    shareRow(ICON_MAIL, 'Email my design', 'Opens your email app', () => emailDesign(ctx)),
    shareRow(ICON_DL, 'Download image', 'PNG rendering file', () => downloadDesignImage(ctx)),
    /* ONLY WHEN THERE IS A SET TO TILE. With one product the sheet IS the single download, so the
       row would be two names for one thing — and it appears exactly when a buyer has been switching
       between products all session, which is when they would look for it. No dialog, no options: the
       row states what it will do and how many. */
    ...((ctx.pickedProducts?.() || []).length > 1
      ? [shareRow(ICON_GRID, `Download all ${(ctx.pickedProducts?.() || []).length} on one image`,
          'Every weight you picked, one PNG', () => downloadAllDesignImages(ctx))]
      : []),
  );
  return openModal({ mount: ctx.el, title: 'Save & share', body, actions: [{ id: 'done', label: 'Done', variant: 'ghost' }] });
}

/* ── §5.4 the quote-saved modal — REMOVED 2026-08-24 ─────────────────────────
 * `openQuoteSavedModal` opened a "Design saved." dialog after every submit. It is gone, replaced
 * by the sent card that now stands in place of the quote form (ui/product-step.js). Ryan: cut it.
 *
 * ⛔ IT ALSO LIED ON FAILURE, which is the part worth remembering. The call site opened it BEFORE
 * consulting `result.ok`, so a send that never reached the worker still told the buyer their
 * design was saved and that our team reviews every one — over a request that did not exist. The
 * failure branch now shows only `status.info`, which says the true thing and names the email
 * address to write to.
 *
 * Deleted rather than kept-and-annotated (the treatment `openShareSheet` above got) because that
 * one is unbuilt-but-wanted and this one is superseded by something shipped. `snapshotBlob` has
 * four other callers and is untouched. */
